import React, { useState, useEffect } from "react";
import { useNavigate, NavLink, Link, useLocation } from "react-router-dom";
import { IoIosArrowUp } from "react-icons/io";
import "./css/navigation.css";
import { useTranslation } from "react-i18next";
import { BiChevronRight } from "react-icons/bi";
import { TbFence } from "react-icons/tb";
import {
  HiOutlineShoppingCart,
  HiOutlineCloudDownload,
  HiOutlineSupport,
  HiOutlineDeviceTablet,
  HiOutlinePlus,
  HiOutlineLocationMarker,
  HiOutlineMap,
  HiOutlineClock,
  HiOutlineShieldCheck,
  HiOutlineCog,
  HiOutlineUser,
  HiOutlineLogout,
  HiOutlineChartBar,
  HiChevronDown,
  HiChevronRight,
} from "react-icons/hi";
import { HiArrowUpRight } from "react-icons/hi2";
import { TbCurrentLocation } from "react-icons/tb";
import { useMutation, useQuery } from "react-query";
import { handleGetUserDetails, removeFCMToken } from "../utils/api";
//PostHog
import { usePostHog } from "posthog-js/react";
import axios from "axios";
import { AuthContext } from "../Context/AuthContext";
import { ShopAllLink, SupportLink } from "../utils/links";

// ======= Nav Links SCHEMA
// t_label: translation label for the link text to be passed to the t()
// link: link path
// screen: either show on "mobile" or on "desktop" or "both"
// proteced: only for logged in users
// subLinks: Array of Links in the the dropdown menu if any
// action: in case there is a non-navigation action on the button/link, should be added to the MainNavLink handleAction() switch block
// icon: React icon component for the menu item

const NAVLINKS = [
  {
    t_label: "navigation.main.shop_trackers",
    link: ShopAllLink,
    screen: "both",
    external: true,
    target: "_blank", // optional
    icon: HiOutlineShoppingCart,
  },
  {
    t_label: "navigation.main.support",
    link: SupportLink,
    screen: "both",
    external: true,
    target: "_blank", // optional
    icon: HiOutlineSupport,
  },
  {
    t_label: "navigation.main.devices",
    link: "/my-devices",
    screen: "mobile",
    protected: true,
    icon: HiOutlineDeviceTablet,
  },
  {
    t_label: "navigation.main.add_tracker",
    link: "/add-device",
    screen: "mobile",
    protected: true,
    icon: HiOutlinePlus,
  },
  {
    t_label: "navigation.main.tracking",
    link: null,
    protected: true,
    screen: "desktop",
    icon: TbCurrentLocation,
    subLinks: [
      {
        t_label: "navigation.main.live",
        link: "/track",
        icon: HiOutlineLocationMarker,
      },
      {
        t_label: "navigation.main.history",
        link: "/history",
        icon: HiOutlineClock,
      },
      {
        t_label: "navigation.main.geofences",
        link: "/my-geofences",
        icon: TbFence,
      },
    ],
  },
  {
    t_label: "navigation.main.manage",
    link: null,
    protected: true,
    screen: "desktop",
    icon: HiOutlineCog,
    subLinks: [
      {
        t_label: "navigation.main.devices",
        link: "/my-devices",
        icon: HiOutlineDeviceTablet,
      },
      {
        t_label: "navigation.main.add_tracker",
        link: "/add-device",
        icon: HiOutlinePlus,
      },
      {
        t_label: "navigation.main.history_management",
        link: "/history-management",
        icon: HiOutlineCloudDownload,
      },
    ],
  },
  {
    t_label: "navigation.main.account",
    link: "/account",
    protected: true,
    sublinksAlignment: "right",
    icon: HiOutlineUser,
    subLinks: [
      {
        t_label: "navigation.main.logout",
        action: "logout",
        icon: HiOutlineLogout,
      },
    ],
  },
];

const Navbar = ({ setLoggedIn, loggedIn }) => {
  const { refreshLogin } = React.useContext(AuthContext);
  const history = useNavigate();
  const posthog = usePostHog();
  const path = useLocation().pathname;

  const [isBusiness, setIsBusiness] = useState(false);
  const [mactive, setMactive] = useState(false);
  const [logo, setLogo] = useState("/assets/branding/name.webp");
  const [webviewCanClose, setWebviewCanClose] = useState(true);
  const { t } = useTranslation();

  const user = useQuery({
    queryKey: ["user-details"],
    queryFn: handleGetUserDetails,
    onSuccess: (data) => {
      setIsBusiness(data?.purpose === "business");
    },
  });

  useEffect(() => {
    if (loggedIn) {
      user.refetch();
    }
  }, [loggedIn]);

  useEffect(() => {
    let stored_logo = localStorage.getItem("business_logo");
    if (stored_logo) setLogo(stored_logo);
  }, []);

  useEffect(() => {
    //Check if the webview_app_version ls key exists
    if (localStorage.getItem("webview_app_version")) {
      setWebviewCanClose(false);
    }
  }, []);

  useEffect(() => {
    setMactive(false);
  }, [path]);

  const handleLogout = async () => {
    try {
      await removeFCMToken();
    } catch (error) {
      if (error.response?.data?.message === "jwt expired") {
        await refreshLogin();
        return handleLogout();
      }
    } finally {
      localStorage.removeItem("fcm_token");
      localStorage.removeItem("safetag-ref_token");
      localStorage.removeItem("safetag-id_token");
      localStorage.removeItem("safetag-username");
      sessionStorage.removeItem("safetag-ref_token");
      setLoggedIn(false);
      posthog.reset();
      history("/login");
    }
  };

  return (
    <>
      <div className="bg-white/95 backdrop-blur-sm fixed top-0 left-0 w-full h-16 z-[9] border-b border-gray-100"></div>
      <div className="w-full flex items-center justify-center sticky top-0 z-10">
        <div className="container">
          <div className="flex items-center justify-between py-3 px-4 ">
            <div className="flex items-center justify-start gap-3 lg:justify-start w-full">
              <Link to="/" className="flex items-center gap-2 group">
                <img
                  className="object-contain h-9 transition-transform duration-200 group-hover:scale-105"
                  src="/assets/branding/shield.webp"
                  alt="SafeTag Logo"
                ></img>
                <img
                  className={
                    "object-contain transition-transform duration-200 group-hover:scale-105 " +
                    (logo === "/assets/branding/name.webp"
                      ? "h-5 md:h-6"
                      : "h-10 md:h-12")
                  }
                  src={logo}
                  alt="SafeTag Name"
                ></img>
              </Link>
            </div>

            <div className="flex flex-row items-center">
              {/* Links */}
              <div
                className={`${
                  mactive ? "top-0" : "top-[-150%]"
                } fixed flex transition-all duration-300 lg:relative h-screen lg:mr-5 lg:h-auto w-full whitespace-nowrap items-start p-6 pt-20 lg:p-0 lg:items-center lg:w-full top-0 lg:top-auto left-0 lg:left-auto bg-white lg:bg-transparent gap-1 lg:gap-0.5 flex-col lg:flex-row shadow-xl lg:shadow-none will-change-[top]`}
              >
                {/* General Navigation Links */}
                {NAVLINKS.filter(
                  (link) => webviewCanClose || !link.external,
                ).map((link, index) => (
                  <MainNavLink
                    link={link}
                    key={index}
                    isLogged={loggedIn}
                    logout={handleLogout}
                    hideNav={() => setMactive(false)}
                    isBusiness={isBusiness}
                  />
                ))}

                {/* Login Register */}
                {false && !loggedIn && (
                  <div className="flex flex-row items-center gap-4 font-medium w-full py-2 lg:py-0 lg:ml-6">
                    <NavLink
                      onClick={() => setMactive(false)}
                      className={({ isActive }) =>
                        isActive
                          ? "px-5 py-2 bg-gray-100 border rounded-md  cursor-pointer flex-1 text-center"
                          : "px-5 py-2 bg-white hover:bg-gray-100 border rounded-md cursor-pointer flex-1 text-center"
                      }
                      to="/login"
                    >
                      {t("navigation.main.login")}
                    </NavLink>
                    <NavLink
                      onClick={() => setMactive(false)}
                      className={({ isActive }) =>
                        isActive
                          ? "text-white  bg-pr px-3 py-2 rounded-md flex-1 text-center"
                          : "text-white  bg-sr hover:bg-pr px-3 py-2 rounded-md flex-1 text-center"
                      }
                      to="/signup"
                    >
                      {t("navigation.main.register")}
                    </NavLink>
                  </div>
                )}

                {/* Logout  */}
                {loggedIn && webviewCanClose && (
                  <div className="flex lg:hidden flex-row items-center justify-center font-medium w-full mt-6">
                    <div
                      className="w-full px-5 py-2.5 bg-white hover:bg-red-50 border border-red-200 text-red-600 rounded-md cursor-pointer text-center transition-all duration-200 flex items-center gap-2 justify-center font-medium hover:border-red-300"
                      onClick={handleLogout}
                    >
                      <HiOutlineLogout className="text-xl" />
                      {t("navigation.main.logout")}
                    </div>
                  </div>
                )}
              </div>

              {/* menu icon */}
              <div
                className="relative h-5 lg:hidden ml-5 w-[23px]"
                onClick={() => setMactive((s) => !s)}
              >
                <div
                  className={`absolute transition-all duration-500 top-0 bg-pr left-0 origin-right w-[23px] h-0.5 ${
                    mactive ? "-rotate-45" : "rotate-0"
                  }`}
                ></div>
                <div
                  className={`w-[23px] h-0.5 ${
                    mactive ? "opacity-0" : "opacity-100"
                  } absolute transition-all duration-200 top-2 bg-pr left-0`}
                ></div>
                <div
                  className={`absolute transition-all duration-500 top-4 bg-pr left-0 origin-right w-[23px] h-0.5 ${
                    mactive ? "rotate-45" : "rotate-0"
                  }`}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Main Nav Links that appear on the navbar
const MainNavLink = ({ link, isLogged, logout, hideNav, isBusiness }) => {
  const { t } = useTranslation();
  const [showSubLinks, setSubLink] = useState(false);

  // action other than navigation
  const handleAction = (action) => {
    switch (action) {
      case "logout":
        logout();
        break;
      default:
        return;
    }
  };

  // Incase there is no link on the main Heading of drop down we use Div eg. Manage
  const isExternal = link.external === true;
  const LinkComponent = isExternal ? "a" : link.link ? NavLink : Div;

  // Filter out the links specified logged out users and logged in users
  if ((link.protected && !isLogged) || (link.logout && isLogged)) return null;
  // === Business Dashboard injection (NO mutation of NAVLINKS) ===
  let computedSubLinks = link.subLinks || [];

  if (link.t_label === "navigation.main.tracking") {
    if (isBusiness) {
      // Add dashboard only once
      const hasDashboard = computedSubLinks.some(
        (l) => l.link === "/dashboard",
      );
      if (!hasDashboard) {
        computedSubLinks = [
          ...computedSubLinks,
          {
            t_label: "navigation.main.dashboard",
            link: "/dashboard",
            icon: HiOutlineChartBar,
          },
        ];
      }
    }
  }

  const Icon = link.icon;

  return (
    <div
      onClick={hideNav}
      className={`relative w-full ${
        link.screen === "mobile"
          ? "block lg:hidden"
          : link.screen === "desktop"
            ? "hidden lg:block"
            : ""
      }`}
    >
      <LinkComponent
        {...(isExternal
          ? {
              href: link.link,
              target: link.target || "_self",
              rel: "noopener",
            }
          : {
              to: link.link,
              style: ({ isActive }) => ({
                color: isActive ? "#8B3333" : "#1f2937",
              }),
            })}
        onMouseOver={() => setSubLink(true)}
        onMouseOut={() => setSubLink(false)}
        className="hover:text-gray-800 w-full whitespace-nowrap group"
      >
        <div className="w-full flex flex-row gap-2 items-center py-3 px-3 lg:px-3 lg:py-2.5 rounded-md lg:rounded font-medium text-gray-800 hover:text-gray-800 hover:bg-black/5 lg:hover:bg-black/5 cursor-pointer transition-all duration-150">
          {Icon && <Icon className="text-xl lg:text-[19px]" />}
          <p className="font-medium">{t(link.t_label)}</p>
          {isExternal && (
            <HiArrowUpRight className="text-base opacity-50 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          )}
          {computedSubLinks.length ? (
            <HiChevronDown
              className={`${
                showSubLinks ? "rotate-180" : "rotate-0"
              } transition-all duration-200 hidden lg:block text-base ml-auto opacity-60`}
            />
          ) : null}
        </div>
      </LinkComponent>
      {computedSubLinks.length ? (
        <div
          className={`py-1 lg:absolute z-5 bg-transparent ${
            link.sublinksAlignment === "right" ? "right-0" : "left-0"
          } hidden lg:block top-[36px] ${
            showSubLinks ? "pointer-events-auto" : "pointer-events-none"
          }`}
          onMouseOver={() => setSubLink(true)}
          onMouseOut={() => setSubLink(false)}
        >
          <div
            className={`${
              showSubLinks
                ? "opacity-1 translate-y-0"
                : "opacity-0 -translate-y-1 hidden lg:block"
            } bg-white rounded-md transition-all duration-200 lg:border border-gray-100 shadow-lg flex-col min-w-[200px] font-medium lg:p-1.5 gap-0 overflow-hidden`}
          >
            {computedSubLinks.map((sublink, index) => (
              <SubLink
                sublink={sublink}
                key={index}
                handleAction={handleAction}
                isLast={index === computedSubLinks.length - 1}
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Links that are shown in the dropdown
const SubLink = ({ sublink, handleAction, isLast }) => {
  const { t } = useTranslation();
  const LinkComponent = sublink.link ? Link : Div;
  const Icon = sublink.icon;
  const isLogout = sublink.action === "logout";

  return (
    <LinkComponent
      to={sublink.link}
      onClick={() => handleAction(sublink.action)}
      className={`${isLast ? "" : ""} ${
        isLogout
          ? "text-red-600 hover:text-red-600 hover:bg-red-50"
          : "text-gray-800 hover:text-gray-800 hover:bg-black/5"
      } px-3 py-2 flex flex-row items-center gap-2.5 rounded cursor-pointer transition-all duration-150 group`}
    >
      {Icon && <Icon className="text-[18px]" />}
      <p className="flex-1 font-medium">{t(sublink.t_label)}</p>
      {!isLogout && (
        <HiChevronRight className="text-gray-800/40 text-lg opacity-0 group-hover:opacity-100 transition-opacity -mr-1" />
      )}
    </LinkComponent>
  );
};

const Div = (props) => {
  const { style, children, ...rest } = props;
  return <div {...rest}>{children}</div>;
};

export default Navbar;
